import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState('system');
    const [isDarkMode, setIsDarkMode] = useState(false);

    // Check system preference
    const getSystemPreference = () => {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    };

    // Apply theme to document
    const applyTheme = (darkMode) => {
        const root = document.documentElement;
        if (darkMode) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        setIsDarkMode(darkMode);
    };

    // Initialize theme on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') || 'system';
        setTheme(savedTheme);

        let shouldUseDark = false;
        if (savedTheme === 'dark') {
            shouldUseDark = true;
        } else if (savedTheme === 'light') {
            shouldUseDark = false;
        } else {
            shouldUseDark = getSystemPreference();
        }

        applyTheme(shouldUseDark);
    }, []);

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            if (theme === 'system') {
                applyTheme(e.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // Theme change handler
    const changeTheme = (newTheme) => {
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);

        let shouldUseDark = false;
        if (newTheme === 'dark') {
            shouldUseDark = true;
        } else if (newTheme === 'light') {
            shouldUseDark = false;
        } else {
            shouldUseDark = getSystemPreference();
        }

        applyTheme(shouldUseDark);
    };

    const toggleTheme = () => {
        const newTheme = isDarkMode ? 'light' : 'dark';
        changeTheme(newTheme);
    };

    const contextValue = {
        theme,
        isDarkMode,
        changeTheme,
        toggleTheme,
        themes: [
            { value: 'system', label: 'System' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' }
        ]
    };

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext; 